import React, {
  ReactNode,
  Ref,
  useContext,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { useHotkeys } from "react-hotkeys-hook";
import { track } from "../../utils/tracking";
import { Icon as LucideIcon } from "../Common/Icons/LucideIcons";
import { Switch } from "../Inputs";
import {
  ButtonWrapper,
  LayerIcon,
  LayerName,
  LayerText,
  LayerWrapper,
  Legend,
  LegendInner,
  Row,
} from "./__styles__/Layer";
import { LayerGroupContext } from "./LayerGroup";
import { Group, GroupId, LayerContext } from "./layers";
import { LayerSectionContext } from "./LayerSection";

interface LayerProps<G extends Group> {
  name: string;
  ariaLabel?: string;
  group: G;
  id: GroupId<G>;
  disabled?: boolean;
  color?: string;
  Icon?: React.FC;
  children?: ReactNode;
  nested?: boolean;
}

const Layer = <G extends Group>({
  name,
  ariaLabel,
  group,
  id,
  disabled = false,
  color,
  Icon,
  children: legend,
  nested = false,
}: LayerProps<G>) => {
  const { toggleLayer, isLayerVisible } = useContext(LayerContext);
  const { show: layerSectionExpanded } = useContext(LayerSectionContext) ?? {};
  const { show: layerGroupExpanded } = useContext(LayerGroupContext) ?? {};
  const layerTextRef: Ref<HTMLSpanElement> = useRef(null);
  const [layerTextOverflows, setLayerTextOverflows] = useState(false);

  const [isExpanded, toggleIsExpanded] = useState(false);

  const handleToggle =
    ({
      id,
      group,
    }: {
      id: LayerProps<G>["id"];
      group: LayerProps<G>["group"];
    }) =>
    (isVisible: boolean) => {
      track("Map Layers Changed", {
        layerId: id,
        layerName: name,
        layerGroup: group,
        visible: isVisible,
      });

      if (id) {
        toggleLayer({ id, group, isVisible });
      }
    };

  const hotkeyRef = useHotkeys<HTMLSpanElement>(
    ["space", "return"],
    () => {
      toggleIsExpanded(!isExpanded);
    },
    { preventDefault: true }
  );

  useLayoutEffect(() => {
    if (layerTextRef.current) {
      setLayerTextOverflows(
        layerTextRef.current.offsetWidth < layerTextRef.current.scrollWidth
      );
    }
  }, []);

  const stopCollapsibleTrigger = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
  };

  const layerTextExpandable = !!legend || layerTextOverflows;
  const layerTextExpanded = (!!legend || layerTextOverflows) && isExpanded;
  const hidden = layerSectionExpanded === false || layerGroupExpanded === false;

  return (
    <LayerWrapper>
      <Row
        onClick={() => {
          toggleIsExpanded(!isExpanded);
        }}
        expandable={layerTextExpandable}
        nested={nested}
      >
        <LayerName
          id={id}
          aria-expanded={layerTextExpanded}
          aria-controls={`${id}-layer-text`}
          tabIndex={!layerTextExpandable || hidden ? -1 : 0}
          onClick={() => {
            toggleIsExpanded(!isExpanded);
          }}
          ref={hotkeyRef}
        >
          {!!Icon && (
            <LayerIcon>
              <Icon />
            </LayerIcon>
          )}
          {!!color && (
            <LayerIcon>
              <LucideIcon
                iconName="circle"
                // component requires a color, but it is ignored since strokeWidth is 0
                color="contentPrimary"
                strokeWidth={0}
                fill={color}
              />
            </LayerIcon>
          )}
          <LayerText
            id={`${id}-layer-text`}
            ref={layerTextRef}
            expanded={layerTextOverflows && isExpanded}
            hasIcon={!!Icon || !!color}
            nested={nested}
            aria-label={ariaLabel ?? name}
            aria-labelledby={id}
            aria-hidden={layerTextExpandable ? hidden : undefined}
          >
            {name}
          </LayerText>
        </LayerName>
        <ButtonWrapper>
          {layerTextExpandable && (
            <LucideIcon
              iconName={isExpanded ? "chevron-up" : "chevron-down"}
              color={"contentSecondary"}
              size={12}
            />
          )}
          <span onClick={stopCollapsibleTrigger}>
            <Switch
              name={`${group}-${id}`}
              onChange={handleToggle({ id, group })}
              value={id ? isLayerVisible({ id, group }) : false}
              size="tiny"
              disabled={disabled}
              tabIndex={hidden ? -1 : 0}
              aria-label={ariaLabel}
            />
          </span>
        </ButtonWrapper>
      </Row>
      {!!legend && (
        <Legend expanded={isExpanded} nested={nested}>
          <LegendInner data-testid={`legend-${name}`}>{legend}</LegendInner>
        </Legend>
      )}
    </LayerWrapper>
  );
};

export default Layer;
